import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";

import type { MeetingLayoutChannel } from "../../application/useMeetingLayout";
import { MEETING_LAYOUTS, type MeetingLayoutId } from "../../domain/meetingLayout";
import MeetingControlMenu from "./MeetingControlMenu";

/**
 * A diagram of each arrangement, so the choice can be seen rather than read.
 *
 * Three phrases that all mean "an arrangement of rectangles" are hard to tell
 * apart in any language; two rectangles in the right places are not. Purely
 * decorative — the name and the line under it are what a screen reader gets.
 */
function LayoutSwatch({ layout }: { layout: MeetingLayoutId }) {
    const frame =
        "relative size-8 shrink-0 overflow-hidden rounded-md border border-room-line bg-room-base";

    if (layout === "gallery") {
        return (
            <span aria-hidden="true" className={`${frame} flex gap-0.5 p-1`}>
                <span className="flex-1 rounded-sm bg-room-ink-muted/60" />
                <span className="flex-1 rounded-sm bg-room-ink-muted/60" />
            </span>
        );
    }

    return (
        <span aria-hidden="true" className={`${frame} p-1`}>
            <span className="block size-full rounded-sm bg-room-ink-muted/60" />
            {layout === "speaker" && (
                <span className="absolute bottom-1 right-1 h-2.5 w-3 rounded-[2px] border border-room-raised bg-room-ink" />
            )}
        </span>
    );
}

/**
 * Who is big, who is small, who is not shown.
 *
 * Real radio inputs rather than buttons with `aria-pressed`: this is one
 * choice out of three, arrow keys should move between them, and a native
 * fieldset gives that with no keyboard handler of our own.
 *
 * Every option carries a name *and* a line saying what it does. The names on
 * their own were the kind of copy that only makes sense to whoever wrote it —
 * you cannot tell "gallery" from "speaker" until you have tried both.
 *
 * What is checked is what is on screen, not what is stored: while somebody is
 * sharing a screen the picture follows the share, and a menu still claiming
 * this person is in the grid would be a menu lying to them. Pressing an option
 * there takes the picture back and holds it until the share ends.
 */
export default function MeetingLayoutPicker({
    channel,
    compact = false,
}: {
    channel: MeetingLayoutChannel;
    /** The phone's version of the control that opens this. */
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const { presented, setLayout, autoFocused } = channel;

    return (
        <MeetingControlMenu
            label={t("meetingLayout.title")}
            testId="meeting-layout"
            icon={<LayoutGrid size={18} aria-hidden="true" />}
            active={presented !== "gallery"}
            compact={compact}
        >
            {(close) => (
            <fieldset>
                <legend className="sr-only">{t("meetingLayout.title")}</legend>

                {/*
                  * Said out loud, because otherwise the checked option is a
                  * layout this person never picked and the menu looks broken.
                  */}
                {autoFocused && (
                    <p className="px-2 pb-1 text-xs leading-snug text-room-ink-muted">
                        {t("meetingLayout.following")}
                    </p>
                )}

                {MEETING_LAYOUTS.map((id) => (
                    <label
                        key={id}
                        className={`room-menu-item items-start py-1.5 ${
                            presented === id ? "bg-room-active" : ""
                        }`}
                    >
                        <input
                            type="radio"
                            name="meeting-layout"
                            value={id}
                            checked={presented === id}
                            onChange={() => {
                            setLayout(id);
                            close();
                        }}
                            className="mt-1.5 size-4 accent-[var(--room-accent)]"
                        />
                        <LayoutSwatch layout={id} />
                        <span className="min-w-0">
                            <span className="block">
                                {t(`meetingLayout.options.${id}.name`)}
                            </span>
                            <span className="block text-xs leading-snug text-room-ink-muted">
                                {t(`meetingLayout.options.${id}.hint`)}
                            </span>
                        </span>
                    </label>
                ))}
            </fieldset>
            )}
        </MeetingControlMenu>
    );
}
